<template>
	<div class="policy-options" v-if='quote'>

		<tw-title description="Confirm your policy details" :email="(quote.client) ? true : false">Your Cover</tw-title>

		<div class="policy-options-content" :class="{ 'h-64': isLoading }">
			<div v-if="!isLoading">
				<div class="bikes">

					<div class="pb-2 border-b border-gray-200 mt-8 mb-5 flex">
						<div class="flex-1">

							<div class="text-2xl font-bold text-gray-700">
								Your bikes
							</div>
							<p class="text-gray-500 font-light">
								Enter the details of the cycles you want to insure
							</p>
						</div>
						<div>
							<div v-if="quote.covered.length < maxBikeLimit && allowedBikeValue > 0">
								<tw-button icon="plus" size="lg" @click="openModal('add-bike')">Add</tw-button>
							</div>
						</div>
					</div>

					<div class="policy-options-content-container">
						<tw-alert :type="feedback.bikes.type" :show="feedback.bikes !== false" :fadeable="true" class="mb-3"
							@close="feedback.bikes = false">
							{{ feedback.bikes.message }}
						</tw-alert>

						<div class="relative">
							<div class="rounded-md bg-red-50 p-4 border border-red-200 mb-5"
								v-if="quote.covered.length > this.maxBikeLimit">
								<div class="flex">
									<div class="flex-shrink-0">
										<svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
											<path fill-rule="evenodd"
												d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
												clip-rule="evenodd" />
										</svg>
									</div>
									<div class="ml-3">
										<h3 class="text-sm leading-5 font-medium text-red-800">
											Maximum bike limit
										</h3>
										<div class="mt-2 text-sm leading-5 text-red-700">
											<p>
												You can get cover for up to 5 bikes. Please ensure you only have up to 5
												bikes assigned to your quote.
											</p>
										</div>
									</div>
								</div>
							</div>

							<div class="rounded-md bg-red-50 p-4 border border-red-200" v-if="!bikesComplete">
								<div class="flex">
									<div class="flex-shrink-0">
										<svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
											<path fill-rule="evenodd"
												d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
												clip-rule="evenodd" />
										</svg>
									</div>
									<div class="ml-3">
										<h3 class="text-sm leading-5 font-medium text-red-800">
											Attention required
										</h3>
										<div class="mt-2 text-sm leading-5 text-red-700">
											<p>
												Please ensure you have filled out all information concerning each of your
												bikes, including make, model and value.
											</p>
										</div>
									</div>
								</div>
							</div>

							<ul class="bikes-container">
								<li :class="(bike.make && bike.model && bike.value && bike.type) ? 'selected' : 'not-complete'"
									v-for="bike in quote.covered">
									<div class="w-full flex items-center justify-between p-6 space-x-6">
										<!-- Generated by IcoMoon.io -->
										<svg version="1.1" xmlns="http://www.w3.org/2000/svg"
											xmlns:xlink="http://www.w3.org/1999/xlink" class="w-10 h-10"
											viewBox="0 0 20 20">
											<path fill="currentColor"
												d="M5.5 9.002c-0.828 0-1.502-0.674-1.502-1.502s0.674-1.502 1.502-1.502 1.502 0.674 1.502 1.502-0.674 1.502-1.502 1.502zM5.5 6.998c-0.277 0-0.502 0.225-0.502 0.502s0.225 0.502 0.502 0.502 0.502-0.225 0.502-0.502-0.225-0.502-0.502-0.502z">
											</path>
											<path fill="currentColor"
												d="M9.5 17c-0.276 0-0.5-0.224-0.5-0.5v-2.998c0-0.772 0.581-1.543 1.324-1.756l1.5-0.429-3.26-2.173-1.71 1.71c-0.094 0.094-0.221 0.146-0.354 0.146h-2.002c-0.276 0-0.5-0.224-0.5-0.5s0.224-0.5 0.5-0.5h1.795l1.854-1.854c0.169-0.169 0.433-0.195 0.631-0.063l3.668 2.445c0.55 0.367 0.548 0.797 0.522 0.966s-0.156 0.579-0.791 0.761l-1.577 0.452c-0.313 0.090-0.599 0.468-0.599 0.795v2.998c0 0.276-0.224 0.5-0.5 0.5z">
											</path>
											<path fill="currentColor"
												d="M15 20c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4zM15 13c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z">
											</path>
											<path fill="currentColor"
												d="M3.999 20c-2.205 0-3.999-1.794-3.999-3.999s1.794-3.999 3.999-3.999 3.999 1.794 3.999 3.999-1.794 3.999-3.999 3.999zM3.999 13.002c-1.654 0-2.999 1.345-2.999 2.999s1.345 2.999 2.999 2.999 2.999-1.345 2.999-2.999-1.345-2.999-2.999-2.999z">
											</path>
										</svg>

										<div class="bike-content-container">
											<div class="bike-title">
												<h3>{{ (bike.name) ? bike.name : 'Untitled bike' }}</h3>
											</div>
											<div class="grid grid-cols-2">
												<div class="bike-column">Price</div>
												<div class="bike-value">
													<span class="value-entered">
														{{ bike.value | currency }}
													</span>
												</div>

												<div class="bike-column">Type</div>
												<div class="bike-value">
													<span class="value-entered" v-if="bike.type">
														{{ bike.type }}
													</span>
													<span v-else>
														Unknown
													</span>
												</div>

												<div class="bike-column">Make</div>
												<div class="bike-value">
													<span class="value-entered" v-if="bike.make">
														{{ bike.make }}
													</span>
													<span v-else>
														Unknown
													</span>
												</div>

												<div class="bike-column">Model</div>
												<div class="bike-value">
													<span class="value-entered" v-if="bike.model">
														{{ bike.model }}
													</span>
													<span v-else>
														Unknown
													</span>
												</div>

												<div class="bike-column">New or secondhand </div>
												<div class="bike-value">
													<span class="value-entered" v-if="bike.purchase_type">
														{{ bike.purchase_type }}
													</span>
													<span v-else>
														Unknown
													</span>
												</div>

												<div class="bike-column">Electric</div>
												<div class="bike-value">
													<span class="value-entered" v-if="bike.is_electric != null">
														{{ bike.is_electric ? 'Yes' : 'No' }}
													</span>
													<span v-else>
														Unknown
													</span>
												</div>


											</div>
										</div>
									</div>
									<div class="bike-buttons">
										<div class="-mt-px flex">
											<div class="bike-button first-button" v-if="quote.covered.length > 1">
												<a v-tw-modal:delete-bike @click="selectBike(bike)" class="-mr-px">
													<svg viewBox="0 0 20 20" stroke="strokeColor" fill="currentColor">
														<path
															d="M6 2l2-2h4l2 2h4v2H2V2h4zM3 6h14l-1 14H4L3 6zm5 2v10h1V8H8zm3 0v10h1V8h-1z" />
													</svg>
													<span class="bike-button-text">Delete</span>
												</a>
											</div>
											<div class="bike-button -ml-px">
												<a v-tw-modal:update-bike @click="selectBike(bike)">
													<svg viewBox="0 0 20 20" stroke="strokeColor" fill="currentColor">
														<path
															d="M12.3 3.7l4 4L4 20H0v-4L12.3 3.7zm1.4-1.4L16 0l4 4-2.3 2.3-4-4z" />
													</svg>
													<span class="bike-button-text">
														<span v-if="(bike.make && bike.model && bike.value && bike.type)">
															Edit
														</span>
														<span v-else>
															<span v-if="quote.covered.length == 1">Click to Complete</span>
															<span v-else>Complete</span>
														</span>
													</span>
												</a>
											</div>
										</div>
									</div>
								</li>
								<li class="flex" v-if='showAdditionalBikesPrompt'>
									<div
										class="h-full flex items-center border-dashed border-2 border-teal-400 bg-gray-50 rounded-lg p-3 px-4 ">
										<div
											class="md:flex flex-col justify-center text-center gap-3 items-center md:py-0 py-6">

											<div class="text-lg font-light text-teal-800 md:mb-0 mb-4  flex-1">
												Add an additional bike to get a <span class="font-semibold under">25%</span>
												premium discount
											</div>

											<tw-button icon="plus" @click="openModal('add-bike')" variant='primary'
												size='lg' :full-width='false' align=''>
												Add
											</tw-button>
										</div>

									</div>
								</li>
							</ul>
							<loading :show="loaders.bikes" variant="mini"></loading>
						</div>
					</div>
				</div>

				<div class="policy-duration-container">

					<div class="pb-2 border-b border-gray-200 mt-20 mb-5 flex">
						<div class="flex-1">
							<div class="text-2xl font-bold text-gray-700">
								Cover dates
							</div>
							<p class="text-gray-500 font-light">
								Confirm the date you would like your policy to commence
							</p>
						</div>
					</div>


					<div class="policy-options-content-container policy-duration-content relative">
						<tw-alert :type="feedback.duration.type" :show="feedback.duration !== false" :fadeable="true"
							class="mb-3" @close="feedback.duration = false">
							{{ feedback.duration.message }}
						</tw-alert>

						<div class="policy-duration-grid desktop-headers">
							<div class="policy-duration-column pb-2">Start date <button class="tooltip-el"
									v-tooltip="'Please select the date you wish this insurance to start.  This can be any date from today up to 45 days in the future.'">?</button>
							</div>
							<div class="policy-duration-column pb-2">End date</div>
							<div></div>
						</div>
						<div class="policy-duration-grid">
							<div class="policy-duration-column">
								<div class="mobile-header">Start date <button class="tooltip-el"
										v-tooltip="'Please select the date you wish this insurance to start.  This can be any date from today up to 45 days in the future.'">?</button>
								</div>
								<datepicker v-model="startDate" :wrapper-class="{ 'input-container': true, 'expand': true }"
									placeholder="Policy start date" input-class="form-input" @selected="startDateChanged"
									:disabled-dates="disabledDates" :open-date="openDate"></datepicker>
							</div>

							<div class="policy-duration-column">
								<div class="mobile-header">End date</div>
								<datepicker v-model="endDate"
									:wrapper-class="{ 'input-container': true, 'expand': true, 'disabled': true }"
									placeholder="Policy end date" input-class="form-input" :disabled="true"></datepicker>
							</div>

							<div class="policy-duration-update-btn">
								<!-- <tw-button @clicked="updateDuration">Update</tw-button> -->
							</div>
						</div>

						<loading :show="loaders.duration" variant="mini"></loading>
					</div>
				</div>

				<div class="policy-product-container">
					<div class="pb-2 border-b border-gray-200 mt-20 mb-5 flex">
						<div class="flex-1">
							<div class="text-2xl font-bold text-gray-700">
								Bike cover
							</div>
							<p class="text-gray-500 font-light">
								Provides cover for Theft, Accidental Damage or Vandalism to your bicycle(s)
							</p>
						</div>
					</div>

					<div class='md:inline-block'>
						<introductory-discount-applied class='md:px-8'></introductory-discount-applied>
					</div>

					<div class="policy-options-content-container policy-products base-cover">
						<div class="policy-product" v-for="product in unselectableProducts"
							:class="(product.option && product.option.value > 0) ? 'selected' : ''">
							<div class="w-1/4">
								<span class="policy-product-button">
									<span class="policy-product-button-content">
										<i class="far fa-check-circle"></i>
									</span>
								</span>
							</div>
							<div class="w-3/4">
								<div class="policy-product-details">
									<div class="flex items-center">
										<div class="w-0 flex-1">
											<dl>
												<dd class="flex items-baseline">
													<div class="policy-product-value">
														{{ product.option.name }}
													</div>
												</dd>
												<dd class="policy-product-price"
													:class="{ 'addition': product.option.value > 0 }">
													<div v-if="quote.introDiscount">{{ quote.introDiscount | currency
													}}</div>
													<div v-else-if="introductionDiscount" class="flex gap-4">
														<span>
															{{ (product.option.value - introductionDiscount.value_raw) |
																currency }}
														</span>
														<span class="line-through text-gray-200">
															{{ product.option.value | currency }}
														</span>
													</div>
													<div v-else>
														{{ product.option.value | currency }}
													</div>
												</dd>
											</dl>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="inline-flex items-center px-3 py-0.5 rounded-sm text-sm font-medium bg-teal-100 text-teal-800"
						v-if="hasIntroOffer">
						<i class="fas fa-info-circle mr-2"></i>
						20% INTRODUCTORY DISCOUNT TO BE APPLIED TO THE ABOVE
					</div>





					<div class="inline-flex items-center px-3 py-0.5 rounded-sm text-sm font-medium bg-teal-100 text-teal-800"
						v-if="multiDiscount">
						<div class="discount-container">
							<div class="flex gap-2 items-center">
								<div class="flex-1">
									<div class="font-light text-base text-teal-700">
										<span class="font-medium uppercase" v-if='multiDiscount.owner_type !== "system"'>
											{{ multiDiscount.code }} -
										</span>
										<span class="font-normal">
											<span v-if='multiDiscount.owner_type === "system"'>
												<i class="fas fa-star text-teal-500"></i>
											</span>
											{{ multiDiscount.name_human }}
										</span>
									</div>
								</div>
								<div class="text-right flex flex-row-reverse">
									<div>
										<div class="font-semibold self-center text-lg text-gray-600">
											- {{ multiDiscount.value }}
										</div>
									</div>
								</div>

							</div>

						</div>
					</div>

				</div>

				<div class="policy-product-container">
					<div class="pb-2 border-b border-gray-200 mt-20 mb-5 flex">
						<div class="flex-1">
							<div class="text-2xl font-bold text-gray-700">
								Optional cover
							</div>
							<p class="text-gray-500 font-light">
								Tailor your policy with the additional covers you need
							</p>
						</div>
					</div>

					<div class="relative">
						<tw-alert :type="feedback.products.type" :show="feedback.products !== false" :fadeable="true"
							class="mb-3" @close="feedback.products = false">
							{{ feedback.products.message }}
						</tw-alert>
						<div class="policy-options-content-container policy-products">

							<div class="policy-product" v-for="product in selectedProducts"
								:class="(product.option && product.option.value > 0) ? 'selected' : ''">
								<div class="w-3/4 relative">
									<div class="policy-product-details">
										<div class='absolute top-2 left-5'>
											<span v-if="product.is_popular"
												:class="['border-teal-600 border font-semibold rounded-full px-3 py-1 text-sm', { 'text-white': product.option && product.option.value > 0, 'text-primary': product.option && product.option.value == 0 }]">
												<i class="fas mr-2 fa-star" />Most Popular
											</span>
										</div>
										<div class="flex mt-4 items-center">
											<div class="w-0 flex-1">
												<dl>
													<dt class="policy-product-name">
														{{ product.name }}
													</dt>
													<dd class="flex items-baseline">
														<div class="policy-product-value">
															{{ product.option.name }}
														</div>
													</dd>
													<dd class="policy-product-price"
														:class="{ 'addition': product.option.value > 0 }">
														<span v-if="(product.option && product.option.value > 0)">{{
															product.option.value | currency }}</span>
														<span v-else>
															<span
																class="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800">
																Prices from {{ product.prices_from | currency }}
															</span>
														</span>
													</dd>
												</dl>
											</div>
										</div>
									</div>
								</div>
								<div class="w-1/4">
									<a class="policy-product-button" @click="selectedProduct = product"
										v-tw-modal:update-product>
										<span v-if="product.option && product.option.value > 0">
											<span class="policy-product-button-content">
												<span>
													<i class="far fa-check-circle"></i>
												</span>
												<span class="mt-2">Edit</span>
											</span>
										</span>
										<span class="policy-product-button-content" v-else>
											Add
										</span>
									</a>
								</div>
							</div>

						</div>
						<loading :show="loaders.products" variant="mini"></loading>
					</div>
				</div>
			</div>
			<loading :show="isLoading"></loading>
		</div>

		<tw-modal ref="add-bike" size="lg" ok-text="Create" @ok="addBike" :ok-busy='loaders.bikes' :manual-close="true">
			<template v-slot:header>
				Add bike
			</template>

			<template v-slot:subtext>
				Add another bike to your quote
			</template>

			<template v-slot:body>
				<div class="bike-input-container">
					<tw-alert type="error" :show="showBikeErrors && createBikeErrors !== false" class="mb-3"
						@close="showBikeErrors = false">
						{{ createBikeErrors[0] }}
					</tw-alert>

					<tw-alert type="error" :show="hasErrors" class="mb-3">
						{{ errorMessage }}
					</tw-alert>

					<tw-form-group label="Type" :errors="errors" field="type">
						<div class="bike-input-container">
							<div class="bike-input">
								<div>
									<tw-select title="" :items="bikeTypes" @updated="newBikeTypeUpdated" placeholder></tw-select>
								</div>
							</div>
						</div>
					</tw-form-group>

					<tw-form-group label="Make" :errors="errors" field="make">
						<div class="bike-input-container">
							<div class="bike-input">
								<input id="bike-make" placeholder="Bike make" aria-describedby="bike-make-description"
									v-model="newBike.make">
							</div>
						</div>
					</tw-form-group>


					<tw-form-group label="Model" :errors="errors" field="model">
						<div class="bike-input-container">
							<div class="bike-input">
								<input id="bike-model" placeholder="Bike model" aria-describedby="bike-model-description"
									v-model="newBike.model">
							</div>
						</div>
					</tw-form-group>

					<tw-form-group label="Value" :errors="errors" field="value">
						<div class="bike-input-container">
							<div class="bike-input">
								<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<span class="text-gray-500 sm:text-sm sm:leading-5">
										£
									</span>
								</div>
								<input id="bike-price" class="with-icon" placeholder="0.00"
									aria-describedby="bike-price-currency" v-model="newBike.value">
								<div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
									<span class="text-gray-500 sm:text-sm sm:leading-5" id="bike-price-currency">
										GBP
									</span>
								</div>
							</div>
						</div>
					</tw-form-group>


					<tw-form-group label="Did you buy it new or secondhand?" :errors="errors" field="purchase_type">
						<div class="flex gap-2">
							<tw-radio-option class='flex-1' v-model="newBike.purchase_type" option='new' title="New">
							</tw-radio-option>

							<tw-radio-option class='flex-1' v-model="newBike.purchase_type" option='secondhand'
								title="Secondhand">
							</tw-radio-option>
						</div>
					</tw-form-group>


					<tw-form-group label="Is the bike electric?" :errors="errors" field="is_electric">
						<div class="flex gap-2">
							<tw-radio-option class='flex-1' v-model="newBike.is_electric" :option='true' title="Yes">
							</tw-radio-option>

							<tw-radio-option class='flex-1' v-model="newBike.is_electric" :option='false' title="No">
							</tw-radio-option>
						</div>
					</tw-form-group>

				</div>
			</template>
		</tw-modal>

		<tw-modal ref="delete-bike" size="lg" @ok="deleteBike" :ok-busy='loaders.bikes' ok-text="delete" :padding="false">
			<template v-slot:header>
				Delete <span class="italic">{{ (selectedBike && selectedBike.name) ? selectedBike.name : 'Untitled bike'
				}}</span>
			</template>

			<template v-slot:subtext>
				Deleting this bike will remove it from your quote
			</template>

			<template v-slot:body>
				<p class="text-gray-500">Are you sure you want to delete this bike?</p>
			</template>

		</tw-modal>

		<tw-modal ref="update-bike" size="lg" @ok="updateBike" :ok-busy='loaders.bikes' :manual-close="true">
			<template v-slot:header>
				Update <span class="italic">{{ (selectedBike && selectedBike.name) ? selectedBike.name : 'Untitled bike'
				}}</span>
			</template>

			<template v-slot:subtext>
				Change the details of an existing bike
			</template>

			<template v-slot:body>
				<div class="bike-input-container">
					<tw-alert type="error" :show="showBikeErrors && bikeErrors !== false" class="mb-3"
						@close="showBikeErrors = false">
						{{ bikeErrors[0] }}
					</tw-alert>

					<tw-alert type="error" :show="hasErrors" class="mb-3">
						{{ errorMessage }}
					</tw-alert>

					<tw-form-group label="Type" :errors="errors" field="type">
						<div class="bike-input-container">
							<div class="bike-input">
								<div>
									<tw-select title="" :items="bikeTypes" @updated="updateBikeTypeUpdated"
										:val="selectedType" placeholder></tw-select>
								</div>
							</div>
						</div>

					</tw-form-group>

					<tw-form-group label="Make" :errors="errors" field="make">
						<div class="bike-input-container">
							<div class="bike-input">
								<input id="bike-make" placeholder="Bike make" aria-describedby="bike-make-description"
									v-model="selectedBike.make">
							</div>
						</div>
					</tw-form-group>


					<tw-form-group label="Model" :errors="errors" field="model">
						<div class="bike-input-container">
							<div class="bike-input">
								<input id="bike-model" placeholder="Bike model" aria-describedby="bike-model-description"
									v-model="selectedBike.model">
							</div>
						</div>
					</tw-form-group>

					<tw-form-group label="Value" :errors="errors" field="value">
						<div class="bike-input-container">
							<div class="bike-input">
								<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<span class="text-gray-500 sm:text-sm sm:leading-5">
										£
									</span>
								</div>
								<input id="bike-price" class="with-icon" placeholder="0.00"
									aria-describedby="bike-price-currency" v-model="selectedBike.value">
								<div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
									<span class="text-gray-500 sm:text-sm sm:leading-5" id="bike-price-currency">
										GBP
									</span>
								</div>
							</div>
						</div>
					</tw-form-group>

					<tw-form-group label="Did you buy it new or secondhand?" :errors="errors" field="purchase_type">
						<div class="flex gap-2">
							<tw-radio-option class='flex-1' v-model="selectedBike.purchase_type" option='new' title="New">
							</tw-radio-option>

							<tw-radio-option class='flex-1' v-model="selectedBike.purchase_type" option='secondhand'
								title="Secondhand">
							</tw-radio-option>
						</div>
					</tw-form-group>


					<tw-form-group label="Is the bike electric?" :errors="errors" field="is_electric">
						<div class="flex gap-2">
							<tw-radio-option class='flex-1' v-model="selectedBike.is_electric" :option='true' title="Yes">
							</tw-radio-option>

							<tw-radio-option class='flex-1' v-model="selectedBike.is_electric" :option='false' title="No">
							</tw-radio-option>
						</div>
					</tw-form-group>

				</div>
			</template>

		</tw-modal>

		<tw-modal ref="update-product" size="xl2" :padding="false" @ok="update">
			<template v-slot:header>
				{{ (selectedProduct) ? selectedProduct.name : null }}
			</template>



			<template v-slot:body>
				<div>
					<p class="mb-2 text-sm text-gray-800">
						{{ (selectedProduct) ? selectedProduct.description : null }}
					</p>
					<div class="product-option-inputs">
						<tw-toggle-group name="product-option" :options="options" :selected="selectedProduct.option.name"
							@updated="updateSelection">
							<template v-for="(opt, index) in options" :slot="'additional_'+index">
								<p class="option-value">{{ opt.value | currency }}</p>
							</template>
						</tw-toggle-group>
					</div>
				</div>
			</template>
		</tw-modal>
		<modal-cover-disclaimer ref="modal-cover-disclaimer"></modal-cover-disclaimer>

	</div>
</template>

<script>

import IntroductoryDiscountApplied from '../inc/IntroductoryDiscountApplied';
import FormErrors from '../../classes/FormErrors'
import ModalCoverDisclaimer from './Components/ModalCoverDisclaimer.vue';


export default {
	name: 'quote.options',
	mixins: [
		FormErrors
	],
	components: {
		IntroductoryDiscountApplied,
		ModalCoverDisclaimer,
	},
	props: {
		quote: {
			required: true,
		},
		loading: {
			type: Boolean,
			default: false,
		}
	},
	data() {
		return {
			selectedProduct: null,
			selectedBike: null,
			isLoading: true,
			disabledDates: {
				from: null,
				to: null,
			},
			openDate: null,
			startDate: '',
			endDate: '',
			newBike: {
				name: '',
				type: '',
				model: '',
				make: '',
				value: '',
			},
			loaders: {
				duration: false,
				bikes: false,
				products: false,
			},
			feedback: {
				duration: false,
				bikes: false,
				products: false,
			},
			showBikeErrors: false,
			maxPerBike: 10000,
			maxPerPolicy: 20000,
			maxBikeLimit: 5,
		}
	},
	computed: {
		showAdditionalBikesPrompt() {
			return this.quote.covered.length < this.maxBikeLimit;
		},
		allowedBikeValue() {
			var bikes = this.quote.covered;

			let remaining = this.maxPerPolicy;
			bikes.forEach((bike) => {
				remaining -= bike.value;
			});

			return (remaining > this.maxPerBike) ? this.maxPerBike : remaining;
		},
		allowedUpdateBikeValue() {

			let remaining = this.maxPerPolicy;

			if (this.selectedBike) {
				var bikes = this.quote.covered.filter((b) => b.id != this.selectedBike.id);
				bikes.forEach((bike) => {
					remaining -= bike.value;
				});

				return (remaining > this.maxPerBike) ? this.maxPerBike : remaining;
			}

			return false;

		},
		selectedProducts() {
			var products = {};

			const self = this;
			Object.keys(self.quote.products.products).forEach(function (key) {
				let productData = self.quote.products.products[key].meta;

				// get selected product (if selectable by user)
				if (productData.is_selectable) {
					products[key] = productData;

					let options = self.quote.products.products[key].options;
					let selected = options.filter(option => option.is_selected == true);
					products[key].option = selected[0];
				}
			});

			return products;
		},
		introductionDiscount() {
			if (this.quote == null) {
				return null;
			}

			return this.quote.discounts.find(d => d.name === 'introduction-discount');
		},
		hasIntroOffer() {
			const self = this;

			let search = self.quote.discounts.filter((d) => d.code == "PMB-INTRO-20");

			if (search.length == 1) {
				return true;
			}

			return false;
		},
		multiDiscount() {
			return this.quote.discounts.find((d) => d.name === "multi-bike-discount:bikeCover");
		},
		unselectableProducts() {
			var products = {};

			const self = this;
			Object.keys(self.quote.products.products).forEach(function (key) {
				let productData = self.quote.products.products[key].meta;

				// get selected product (if selectable by user)
				if (!productData.is_selectable) {
					products[key] = productData;

					let options = self.quote.products.products[key].options;
					let selected = options.filter(option => option.is_selected == true);
					products[key].option = selected[0];
				}
			});

			return products;
		},
		options() {
			var options = false;

			if (this.selectedProduct) {
				options = this.quote.products.products[this.selectedProduct.slug];
			}

			return options.options;
		},
		startDateFormatted() {
			var startYear = this.startDate.getFullYear();
			var startMonth = ((this.startDate.getMonth().length == 1) ? '0' : '') + (this.startDate.getMonth() + 1);
			var startDay = ((this.startDate.getDate().length == 1) ? '0' : '') + this.startDate.getDate();

			return [startYear, (startMonth), startDay].join('-');
		},
		endDateFormatted() {
			var endYear = this.endDate.getFullYear();
			var endMonth = ((this.endDate.getMonth().length == 1) ? '0' : '') + (this.endDate.getMonth() + 1);
			var endDay = ((this.endDate.getDate().length == 1) ? '0' : '') + this.endDate.getDate();

			return [endYear, (endMonth), endDay].join('-');
		},
		bikesComplete() {
			var complete = false;

			if (this.quote) {
				var bikes = this.quote.covered;

				complete = true;
				bikes.forEach((bike) => {
					if (!bike.make || !bike.model || !bike.value) {
						complete = false;
					}
				});
			}

			return complete;
		},
		createBikeErrors() {
			var check = false;

			var errors = [];
			if (this.newBike) {
				check = true;

				if (this.newBike.type == this.bikeTypes[0].name) {
					errors.push('Please enter a bike type');
				}

			}

			return errors.length > 0 ? errors : false;
		},
		bikeErrors() {
			var check = false;

			var errors = [];
			if (this.selectedBike) {
				check = true;

				if (this.selectedBike.type == this.bikeTypes[0].name) {
					errors.push('Please enter a bike type');
				}

			}

			return errors.length > 0 ? errors : false;
		},
		isComplete() {
			return (this.bikesComplete && this.quote.covered.length <= 5) ? true : false;
		},
		bikeTypes() {
			let types = [];

			this.$parent.details.bikeTypes.forEach((type) => {
				let typeId = type.toLowerCase().replace(" ", "-");

				types.push({
					id: typeId,
					name: type,
					value: type,
				});
			});

			return types;
		},
		selectedType() {
			let existingType = this.selectedBike.type;

			let existingTypeArr = this.bikeTypes.filter((b) => b.name == existingType);

			if (existingTypeArr.length > 0) {
				return existingTypeArr[0];
			}

			return false;
		},
	},
	watch: {
		quote: function (newQuote, oldQuote) {
			// start/end cover dates
			this.startDate = new Date(newQuote['cover-start']);
			this.endDate = new Date(newQuote['cover-end']);

			// if bikes are fully completed - push to parent
			this.$emit('refresh', { passed: this.isComplete });
		},
		loading: function (newLoading, oldLoading) {
			this.isLoading = newLoading;
		}
	},
	mounted() {
		this.isLoading = this.loading;

		this.$emit('refresh', { passed: this.isComplete });

		// set minimum start date
		let today = new Date(); // set hours to fix datepicker issue with hours
		today.setHours(0, 0, 0, 0);
		let tomorow = new Date(today);
		tomorow.setDate(tomorow.getDate() + 1);
		this.disabledDates.to = today;

		// set maximum start date
		let maxDate = new Date();
		maxDate.setDate(maxDate.getDate() + 45);
		this.disabledDates.from = maxDate;

		// set open date
		this.openDate = new Date();

		this.startDate = new Date(this.quote['cover-start']);
		this.endDate = new Date(this.quote['cover-end']);
	},
	methods: {
		updateSelection(e) {
			this.selectedProduct.option = e;
		},
		selectBike(b) {
			this.resetErrors();
			this.selectedBike = Object.assign({}, b);
		},
		newBikeTypeUpdated(e) {
			this.newBike.type = e.value;
		},
		updateBikeTypeUpdated(e) {
			this.selectedBike.type = e.value;
		},
		update() {
			const self = this;

			// show loading gif
			self.loaders.products = true;

			var updateArr = {
				'premium_id': self.selectedProduct.option.premium_id
			};
			Vue.axios.post('quote/products', updateArr)
				.then((resp) => {
					if (resp.data) {
						// stop loading gif
						self.loaders.products = false;

						// push back quote to parent
						self.$emit('refresh', { quote: resp.data });

						// push to parent component to display feedback
						self.feedback.products = {
							'type': 'success',
							'message': 'Product option has been updated'
						};
					}
				})
				.catch(error => {
					// stop loading gif
					self.loaders.products = false;
				});
		},
		startDateChanged(newDate) {
			// update disabled dates on cover end
			var start = newDate;
			var startYear = start.getFullYear();
			var startMonth = start.getMonth();
			var startDay = start.getDate();
			var endDate = new Date(startYear + 1, startMonth, startDay - 1);

			// update cover end date
			this.startDate = newDate;
			this.endDate = endDate;

			const self = this;

			// show loading gif
			self.loaders.duration = true;

			var updateArr = {
				'cover_start': self.startDateFormatted,
				'cover_end': self.endDateFormatted,
			};
			Vue.axios.post('quote/duration', updateArr)
				.then((resp) => {
					// stop loading gif
					self.loaders.duration = false;

					if (resp.data.success) {
						// push back quote to parent
						self.$emit('refresh', { quote: resp.data.quote });

						// show feedback
						self.feedback.duration = {
							'type': 'success',
							'message': 'Policy duration has been updated'
						};
					} else {
						// show feedback
						self.feedback.duration = {
							'type': 'error',
							'message': resp.data.feedback.message
						};
					}
				})
		},
		addBike() {
			const self = this;

			this.resetErrors();

			this.$refs['modal-cover-disclaimer'].open({
				form: this.newBike,
			}).then(result => {
				if (result === true) {
					self.loaders.bikes = true;

					Vue.axios.post('quote/covered/', self.newBike)
						.then((resp) => {

							if (resp.data) {
								// clear form
								self.newBike = {
									name: '',
									type: '',
									model: '',
									make: '',
									value: '',
								};

								self.$refs['add-bike'].close();
								self.$emit('refresh', { quote: resp.data, passed: self.isComplete });
								self.feedback.bikes = {
									'type': 'success',
									'message': 'Bike has been added'
								};
							}

							self.loaders.bikes = false;
						})
						.catch(error => {
							self.loaders.bikes = false;
							this.setErrors(error);
						});
				}
			});
		},
		deleteBike() {
			const self = this;

			// show loading gif
			self.loaders.bikes = true;

			// delete bike from quote
			Vue.axios.delete('quote/covered/' + self.selectedBike.id)
				.then((resp) => {
					if (resp.data) {
						if (resp.data) {
							// stop loading gif
							self.loaders.bikes = false;

							// push back quote to parent
							self.$emit('refresh', { quote: resp.data, passed: self.isComplete });

							// show feedback to client
							self.feedback.bikes = {
								'type': 'success',
								'message': 'Bike has been removed'
							};
						}
					}
				})
				.catch(error => {
					// stop loading gif
					self.loaders.bikes = false;
				});
		},
		showDisclaimer(action, form) {


			this.$refs['modal-disclaimer'].open();
		},
		updateBike() {
			const self = this;

			this.resetErrors();

			this.$refs['modal-cover-disclaimer'].open({
				form: this.selectedBike,
			}).then(result => {
				if (result === true) {

					self.loaders.bikes = true;

					Vue.axios.put('quote/covered/' + self.selectedBike.id, self.selectedBike)
						.then((resp) => {
							if (resp.data) {
								self.loaders.bikes = false;
								self.$refs['update-bike'].close();
								self.$emit('refresh', { quote: resp.data, passed: self.isComplete });
								self.feedback.bikes = {
									'type': 'success',
									'message': 'Bike has been updated'
								};
							}
						})
						.catch(error => {
							self.loaders.bikes = false;
							this.setErrors(error);
						});

				}

			});


		},
		openModal(modal) {
			this.selectedBike = {};
			this.resetErrors();
			this.$refs[modal].open();
		}
	}
}
</script>
